import { format, parseISO } from 'date-fns'

/**
 * Formats a given ISO date string according to a specified format.
 *
 * This function parses the ISO 8601 date string into a JavaScript Date object,
 * optionally processes the Date object with a custom function (`dateProcessFn`),
 * and then formats it according to the provided format string, ignoring the client's
 * local time settings.
 *
 * @param {string} date - The ISO 8601 date string to be formatted (e.g., "2024-08-01T00:00:00Z").
 * @param {string} dateFormat - The format string for the output date (e.g., "yyyy-MM-dd'T'HH:mm:ss").
 * @param {Function} [dateProcessFn] - An optional function to process the parsed Date object
 * before formatting (e.g., `startOfDay`, `endOfDay`). If not provided, the date is formatted directly.
 * @returns {string} The formatted date string.
 *
 * @example
 * // Returns "2024-08-01T00:00:00"
 * formatDateIgnoringClientTime("2024-08-01T00:00:00", "yyyy-MM-dd'T'HH:mm:ss");
 *
 * @example
 * // Returns "2024-08-01T23:59:59"
 * formatDateIgnoringClientTime("2024-08-01", "yyyy-MM-dd'T'HH:mm:ss", endOfDay);
 */
export const formatDateIgnoringClientTime = (
  date,
  dateFormat,
  dateProcessFn
) => {
  const parsedDate = parseISO(date.replace('Z', ''))
  const formatter = (date) => format(date, dateFormat)

  return dateProcessFn
    ? formatter(dateProcessFn(parsedDate))
    : formatter(parsedDate)
}
